import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LoadingSpinner } from '@/components/loading-spinner';
import { useNotification } from '@/components/notifications';
import { useTestConnection } from '@/hooks/mutations/encryption/use-test-connection';
import { useConfigureEncryption } from '@/hooks/mutations/encryption/use-configure-encryption';
import {
  ConfigureEncryptionRequest,
  EncryptionStatus,
} from '@/services/encryption/encryption-types';
import { CheckCircle, X, XCircle } from 'lucide-react';
import * as Sentry from '@sentry/react';

interface EncryptionConfigTabProps {
  existingStatus?: EncryptionStatus;
  onSaveSuccess: () => void;
}

export const EncryptionConfigTab = ({
  existingStatus,
  onSaveSuccess,
}: EncryptionConfigTabProps) => {
  const notification = useNotification();
  const testConnection = useTestConnection();
  const configureEncryption = useConfigureEncryption();

  const isEditing = existingStatus?.configured === true;
  const isDev = import.meta.env.DEV;

  const [vaultUrl, setVaultUrl] = useState(existingStatus?.vault_url ?? '');
  const [tenantId, setTenantId] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [keyName, setKeyName] = useState(existingStatus?.key_name ?? '');
  const [skipValidation, setSkipValidation] = useState(false);

  useEffect(() => {
    if (existingStatus?.configured) {
      setVaultUrl(existingStatus.vault_url ?? '');
      setKeyName(existingStatus.key_name ?? '');
    }
  }, [existingStatus]);

  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const [saveError, setSaveError] = useState<string | null>(null);

  const getFormData = (
    opts: { forSave?: boolean } = {},
  ): ConfigureEncryptionRequest => {
    const data: ConfigureEncryptionRequest = {
      vault_url: vaultUrl.trim(),
      tenant_id: tenantId.trim(),
      client_id: clientId.trim(),
      key_name: keyName.trim(),
    };
    const secret = clientSecret.trim();
    if (secret) {
      data.client_secret = secret;
    }
    if (opts.forSave && isDev && skipValidation) {
      data.skip_validation = true;
    }
    return data;
  };

  const isFormValid = () => {
    const data = getFormData();
    return (
      data.vault_url !== '' &&
      data.tenant_id !== '' &&
      data.client_id !== '' &&
      (isEditing ? true : !!clientSecret.trim()) &&
      data.key_name !== ''
    );
  };

  const handleTestConnection = async () => {
    if (!isFormValid()) {
      notification.error('Please fill in all required fields');
      return;
    }

    if (!clientSecret.trim()) {
      notification.error('Client secret is required to test the connection');
      return;
    }

    setTestResult(null);
    try {
      const result = await testConnection.mutateAsync(getFormData());
      setTestResult({ success: result.success, message: result.message });
    } catch (error) {
      Sentry.captureException(error, {
        extra: { context: 'encryption-test-connection' },
      });
      setTestResult({
        success: false,
        message:
          error instanceof Error ? error.message : 'Connection test failed',
      });
    }
  };

  const handleSave = async () => {
    if (!isFormValid()) {
      notification.error('Please fill in all required fields');
      return;
    }

    setSaveError(null);
    try {
      await configureEncryption.mutateAsync(getFormData({ forSave: true }));
      notification.success('Encryption configured successfully');
      onSaveSuccess();
    } catch (error) {
      Sentry.captureException(error, {
        extra: { context: 'encryption-configure-save' },
      });
      setSaveError(
        error instanceof Error ? error.message : 'Failed to save configuration',
      );
    }
  };

  return (
    <div className='space-y-6 max-w-lg'>
      <div className='space-y-2'>
        <Label htmlFor='encryption-vault-url'>Vault URL</Label>
        <Input
          id='encryption-vault-url'
          placeholder='https://your-vault.vault.azure.net'
          value={vaultUrl}
          onChange={(e) => setVaultUrl(e.target.value)}
        />
      </div>

      <div className='space-y-2'>
        <Label htmlFor='encryption-tenant-id'>Tenant ID</Label>
        <Input
          id='encryption-tenant-id'
          placeholder='Azure AD Tenant ID'
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
        />
      </div>

      <div className='space-y-2'>
        <Label htmlFor='encryption-client-id'>Client ID</Label>
        <Input
          id='encryption-client-id'
          placeholder='Azure AD Application Client ID'
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
        />
      </div>

      <div className='space-y-2'>
        <Label htmlFor='encryption-client-secret'>Client Secret</Label>
        <Input
          id='encryption-client-secret'
          type='password'
          placeholder={
            isEditing
              ? 'Leave blank to keep existing'
              : 'Azure AD Client Secret'
          }
          value={clientSecret}
          onChange={(e) => setClientSecret(e.target.value)}
        />
      </div>

      <div className='space-y-2'>
        <Label htmlFor='encryption-key-name'>Key Name</Label>
        <Input
          id='encryption-key-name'
          placeholder='envelope-encryption-key'
          value={keyName}
          onChange={(e) => setKeyName(e.target.value)}
        />
      </div>

      {testResult && (
        <div
          className={`relative flex items-start gap-2 rounded-md border p-3 pr-10 text-sm ${
            testResult.success
              ? 'border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-200'
              : 'border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200'
          }`}
        >
          {testResult.success ? (
            <CheckCircle className='size-4 shrink-0 mt-0.5' />
          ) : (
            <XCircle className='size-4 shrink-0 mt-0.5' />
          )}
          <span className='break-words'>{testResult.message}</span>
          <button
            type='button'
            aria-label='Dismiss'
            onClick={() => setTestResult(null)}
            className='absolute top-2 right-2 inline-flex size-6 items-center justify-center rounded opacity-70 hover:opacity-100 hover:bg-black/5 dark:hover:bg-white/10 focus:outline-none'
          >
            <X className='size-4' />
          </button>
        </div>
      )}

      {saveError && (
        <div className='relative rounded-md border border-red-200 bg-red-50 p-3 pr-10 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200'>
          <div className='space-y-1'>
            <p className='font-medium'>Could not save configuration</p>
            <p className='break-words'>{saveError}</p>
          </div>
          <button
            type='button'
            aria-label='Dismiss'
            onClick={() => setSaveError(null)}
            className='absolute top-2 right-2 inline-flex size-6 items-center justify-center rounded opacity-70 hover:opacity-100 hover:bg-black/5 dark:hover:bg-white/10 focus:outline-none'
          >
            <X className='size-4' />
          </button>
        </div>
      )}

      {isDev && (
        <div className='rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200'>
          <label className='flex items-start gap-2 cursor-pointer'>
            <input
              type='checkbox'
              checked={skipValidation}
              onChange={(e) => setSkipValidation(e.target.checked)}
              className='mt-0.5 cursor-pointer'
            />
            <span>
              <span className='font-medium'>
                Dev mode: skip Key Vault validation on save
              </span>
              <span className='block opacity-80'>
                Only visible in dev builds. Backend honors this only when
                ENVIRONMENT=local. Persists the config without a real Azure
                round-trip.
              </span>
            </span>
          </label>
        </div>
      )}

      <div className='flex items-center gap-3 pt-2'>
        <Button
          variant='outline'
          onClick={handleTestConnection}
          disabled={
            !isFormValid() || testConnection.isPending || !clientSecret.trim()
          }
        >
          {testConnection.isPending ? (
            <>
              <LoadingSpinner size='sm' /> Testing...
            </>
          ) : (
            'Test Connection'
          )}
        </Button>
        <Button
          onClick={handleSave}
          disabled={!isFormValid() || configureEncryption.isPending}
        >
          {configureEncryption.isPending ? (
            <>
              <LoadingSpinner size='sm' /> Saving...
            </>
          ) : (
            'Save'
          )}
        </Button>
      </div>
    </div>
  );
};
